import { useRef, useState } from 'react';
import { financeGet, type FinanceCtx, type FinanceMember } from '../financeApi';

export type DonorValue = { memberId: number | null; donorName: string };

// 헌금자 선택: 교적(성도 명부)에서 이름·연락처로 검색해 연결한다. 명부에 없는 사람은 이름만 직접 입력할 수 있다
// (이름만 있는 헌금은 기부금 영수증 합산에서 '성도 연결 필요'로 따로 표시된다).
// 입력칸을 비우려면 부모가 key를 바꿔 새로 그린다.
export default function MemberPicker({ ctx, value, onChange, autoFocus }: {
  ctx: FinanceCtx;
  value: DonorValue;
  onChange: (value: DonorValue) => void;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState(value.memberId ? '' : value.donorName);
  const [results, setResults] = useState<FinanceMember[]>([]);
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const search = (q: string) => {
    setQuery(q);
    onChange({ memberId: null, donorName: q.trim() });
    window.clearTimeout(timer.current);
    if (!q.trim()) {
      setResults([]);
      return;
    }
    timer.current = window.setTimeout(() => {
      financeGet<FinanceMember[]>(ctx, '/members', { q: q.trim() })
        .then((data) => {
          setResults(data ?? []);
          setOpen(true);
        })
        .catch((err) => console.error('GET /finance/members', err));
    }, 250);
  };

  if (value.memberId) {
    return (
      <div className="fin-picked">
        <span>👤 <b>{value.donorName}</b> <small>교적 연결됨</small></span>
        <button type="button" className="manage-btn manage-btn--sm" onClick={() => onChange({ memberId: null, donorName: '' })}>
          변경
        </button>
      </div>
    );
  }

  return (
    <div className="fin-picker">
      <input
        value={query}
        autoFocus={autoFocus}
        placeholder="이름 또는 연락처로 성도 검색"
        onChange={(e) => search(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
      />
      {open && query.trim() && (
        <div className="fin-picker-list">
          {results.map((m) => (
            <button
              type="button"
              key={m.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange({ memberId: m.id, donorName: m.name });
                setOpen(false);
              }}
            >
              <b>{m.name}</b>
              <span>{[m.phone, m.birthDate?.slice(0, 4) && `${m.birthDate.slice(0, 4)}년생`, m.department, m.status !== '재적' && m.status].filter(Boolean).join(' · ')}</span>
            </button>
          ))}
          {results.length === 0 && <div className="fin-picker-empty">교적에 없는 이름입니다. 이름만 기록됩니다.</div>}
        </div>
      )}
    </div>
  );
}
